import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { CustomButton, icon, InfiniteList2 } from "../../../../../../common/components";
import { FiltersWrapper } from "../../../../../../common/components/filters5";
import ListWithRequest from "../../../../../../common/components/filters5/filters/list-with-request/ListWithRequest";
import { translate } from "../../../../../../common/providers";
import { useApi } from "../../../../../../common/hooks";
import { CommitmentDefinitionService } from "../../../../../../api";
import styles from "./CommitmentDefinitionPanel.module.css";
import CommitmentDefinitionRow from "../commitment-definition-row/CommitmentDefinitionRow";
import CommitmentDefinitionCreation from "../commitment-definition-creation/CommitmentDefinitionCreation";
import CommitmentDefinitionHeaders from "../commitment-definition-headers/CommitmentDefinitionHeaders";

export default function CommitmentDefinitionPanel({ onOpenThemeManagement }) {
	const [filters, setFilters] = useState({});
	const [appliedFilters, setAppliedFilters] = useState({});
	const [openCreation, setOpenCreation] = useState(false);
	const [editingDefinition, setEditingDefinition] = useState(null);
	const [sortDirection, setSortDirection] = useState("asc");
	const [sortKey, setSortKey] = useState("themeName");
	const [refreshCount, setRefreshCount] = useState(0);
	const [infiniteListRequest, setInfiniteListRequest] = useState(null);

	const { call: getAllDefinitions } = useApi(CommitmentDefinitionService.getAllCommitmentDefinitions);
	const { call: deleteDefinition } = useApi(CommitmentDefinitionService.deleteCommitmentDefinition);
	const { call: getAllThemes } = useApi(CommitmentDefinitionService.getAllThemes);

	const selectedThemeIds = useMemo(() => {
		const themeFilter = appliedFilters.themes;
		if (!themeFilter) {
			return [];
		}
		if (Array.isArray(themeFilter)) {
			return themeFilter.map((t) => (typeof t === "object" ? t.id || t.value : t)).filter(Boolean);
		}
		if (themeFilter.elements && Array.isArray(themeFilter.elements)) {
			return themeFilter.elements.map((t) => (typeof t === "object" ? t.id || t.value : t)).filter(Boolean);
		}
		return [];
	}, [appliedFilters]);

	useEffect(() => {
		const searchTerm = appliedFilters.content || "";
		setInfiniteListRequest(
			() =>
				({ page, limit }) =>
					getAllDefinitions({
						page,
						size: limit,
						name: searchTerm,
						themeIds: selectedThemeIds.length > 0 ? selectedThemeIds : undefined,
					})
		);
	}, [getAllDefinitions, appliedFilters, selectedThemeIds, refreshCount, sortDirection]);

	const handleApplyFilters = useCallback((newFilters) => {
		setFilters(newFilters);
		setAppliedFilters(newFilters);
	}, []);

	const handleRemoveFilters = useCallback(
		(newFilters) => {
			handleApplyFilters(newFilters);
		},
		[handleApplyFilters]
	);

	const fetchThemesRequest = useCallback(async () => {
		try {
			const data = await getAllThemes();
			if (!Array.isArray(data)) {
				return [];
			}
			const items = [];
			data.forEach((theme) => {
				items.push({
					id: theme.id,
					name: theme.name,
					icon: icon.faSwatchbook,
				});
			});
			return items;
		} catch (error) {
			console.error("Error fetching themes:", error);
			return [];
		}
	}, [getAllThemes]);

	const filtersRenderer = useCallback(
		() => [
			{
				default: true,
				enabled: true,
				hasAndOrOperator: true,
				component: {
					filterKey: "themes",
					label: translate("dsi.commitment-definition.filter.themes"),
					iconName: icon.faFilter,
				},
				renderer: <ListWithRequest identifier="id" labelIdentifier="name" request={fetchThemesRequest} />,
			},
		],
		[fetchThemesRequest]
	);

	const handleOpenCreation = () => {
		setEditingDefinition(null);
		setOpenCreation(true);
	};

	const handleCloseCreation = () => {
		setOpenCreation(false);
		setEditingDefinition(null);
	};

	const handleEdit = (definition) => {
		setEditingDefinition(definition);
		setOpenCreation(true);
	};

	const handleDelete = (definition) => {
		deleteDefinition({ id: definition.id })
			.then(() => setRefreshCount((prev) => prev + 1))
			.catch(console.error);
	};

	const handleRefresh = () => {
		setRefreshCount((prev) => prev + 1);
	};

	const handleClickSort = (newKey) => {
		if (newKey === sortKey) {
			setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
		} else {
			setSortKey(newKey);
			setSortDirection("asc");
		}
	};

	const rowRenderer = (definition) => (
		<CommitmentDefinitionRow
			key={definition.id}
			definition={definition}
			onDelete={handleDelete}
			onEdit={handleEdit}
			onRefresh={handleRefresh}
		/>
	);

	return (
		<div className={styles.main}>
			<div className={styles.header}>
				<FiltersWrapper
					hasSearch
					className={styles.filtersWrapper}
					components={filtersRenderer()}
					defaultFilters={filters}
					searchClassName={styles.searchClassName}
					searchPlaceHolder={translate("common:btn.search")}
					onApply={handleApplyFilters}
					onRemove={handleRemoveFilters}
				/>
				<div className={styles.actions}>
					<CustomButton color="secondary" variant="outlined" onClick={onOpenThemeManagement}>
						{translate("dsi.commitment-definition.manage-themes")}
					</CustomButton>
					<CustomButton
						color="primary"
						startIcon={icon.faPlus}
						variant="contained"
						onClick={handleOpenCreation}
					>
						{translate("dsi.commitment-definition.new")}
					</CustomButton>
				</div>
			</div>
			<CommitmentDefinitionHeaders
				sortDirection={sortDirection}
				sortKey={sortKey}
				onClickSort={handleClickSort}
			/>
			<div className={styles.list}>
				<InfiniteList2 request={infiniteListRequest} rowRenderer={rowRenderer} />
			</div>
			<CommitmentDefinitionCreation
				definition={editingDefinition}
				open={openCreation}
				onClose={handleCloseCreation}
				onRefresh={handleRefresh}
			/>
		</div>
	);
}

CommitmentDefinitionPanel.propTypes = {
	onOpenThemeManagement: PropTypes.func.isRequired,
};
